'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useToasts } from '@headless-workspace/core-ui';
import { GlowClickable, GlowDivider, GlowTextBody, IconName } from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import {
    ProductReview,
    reportReviewServerAction,
    voteForReviewServerAction,
    VotingType,
    VotingValue,
} from '../../../../../server';
import { VoteButton } from './VoteButton';

type ReviewReportingContainerProps = {
    reviewId: ProductReview['id'];
    vote: ProductReview['vote'];
};

export const ReviewReportingContainer: Component<ReviewReportingContainerProps> = ({ reviewId, vote }) => {
    const t = useTranslations('Discover.Pdp.ratingAndReviews');
    const { notifySuccess } = useToasts();

    const [hasUserSubmittedReview, setHasUserSubmittedReview] = useState(false);
    const [currentVote, setCurrentVote] = useState<VotingType>();
    const [likeCounter, setLikeCounter] = useState(vote?.like ?? 0);
    const [dislikeCounter, setDislikeCounter] = useState(vote?.dislike ?? 0);

    const [hasUserReportedReview, setHasUserReportedReview] = useState(false);

    const onVoteClick = useCallback(
        (votingType: VotingType) => {
            if (!hasUserSubmittedReview && reviewId) {
                setHasUserSubmittedReview(true);
                void voteForReviewServerAction(reviewId, votingType);
                setCurrentVote(votingType);
                if (votingType === VotingValue.Negative) {
                    setDislikeCounter(dislikeCounter + 1);
                } else {
                    setLikeCounter(likeCounter + 1);
                }
            }
        },
        [dislikeCounter, hasUserSubmittedReview, likeCounter, reviewId],
    );
    const onPositiveVoteClick = useCallback(() => onVoteClick(VotingValue.Positive), [onVoteClick]);
    const onNegativeVoteClick = useCallback(() => onVoteClick(VotingValue.Negative), [onVoteClick]);

    const onReportClick = useCallback(() => {
        if (!hasUserReportedReview && reviewId) {
            setHasUserReportedReview(true);
            void reportReviewServerAction(reviewId);
            notifySuccess(t('action.contentReportedSuccessfully'));
        }
    }, [hasUserReportedReview, notifySuccess, reviewId, t]);

    return (
        <>
            <GlowDivider />
            <div className={'flex flex-col gap-0.5 px-1'}>
                <GlowTextBody text={t('label.isReviewUseful')} />
                <div className={'flex flex-row gap-0.5 w-full'}>
                    <VoteButton
                        iconName={IconName.Like}
                        onClick={onPositiveVoteClick}
                        vote={likeCounter}
                        disabled={hasUserSubmittedReview}
                        isVoted={currentVote === VotingValue.Positive}
                    />
                    <VoteButton
                        iconName={IconName.Dislike}
                        onClick={onNegativeVoteClick}
                        vote={dislikeCounter}
                        disabled={hasUserSubmittedReview}
                        isVoted={currentVote === VotingValue.Negative}
                    />
                </div>
            </div>
            <GlowClickable
                className={'px-1'}
                content={{
                    labelElement: (
                        <GlowTextBody
                            text={hasUserReportedReview ? t('action.contentReported') : t('action.reportContent')}
                            textDecoration={'underline'}
                            TagName={'span'}
                        />
                    ),
                }}
                disabled={hasUserReportedReview}
                onClick={onReportClick}
            />
        </>
    );
};
